<template>
    <div>
        <section class="space-y-6">
            <nio-input-group :label="$t('configs.rightPanel.messages.unsubscribeMessage')">
                <template #after-label>
                    <nio-icon class="ml-1" name="bold/question-help-circle" v-tooltip="$t('configs.rightPanel.messages.unsubscribeMessageTooltip')" />
                    <language-chooser class="link-gray | ml-3" :locales="locales" v-model="activeLocale" />
                </template>
                <nio-textarea rows="3" v-model="user.options.unsubscribe_message[activeLocale]" />
                <nio-alert class="mt-4" type="warning" v-if="displayUnsubscribeWarning">
                    <p v-text="$t('configs.rightPanel.messages.unsubscribeMessageWarning')" />
                </nio-alert>
            </nio-input-group>
            <nio-input-group :label="$t('configs.rightPanel.messages.confirmationMessage')">
                <template #after-label>
                    <nio-icon class="ml-1" name="bold/question-help-circle" v-tooltip="$t('configs.rightPanel.messages.confirmationMessageTooltip')" />
                    <language-chooser class="link-gray | ml-3" :locales="locales" v-model="activeLocale" />
                </template>
                <nio-textarea rows="3" v-model="user.options.confirmation_message[activeLocale]" />
            </nio-input-group>
            <nio-input-group :label="$t('configs.rightPanel.messages.paymentMessage')" v-if="showPaymentMessage">
                <template #after-label>
                    <nio-icon class="ml-1" name="bold/question-help-circle" v-tooltip="$t('configs.rightPanel.messages.paymentMessageTooltip')" />
                    <language-chooser class="link-gray | ml-3" :locales="locales" v-model="activeLocale" />
                </template>
                <nio-textarea rows="3" v-model="user.options.payment_message[activeLocale]" />
            </nio-input-group>

            <div class="flex flex-col space-y-4" v-if="user.related_id">
                <nio-input-group :label="$t('configs.rightPanel.messages.crmAppointmentConfirmation')">
                    <template #after-label>
                        <nio-icon class="ml-1" name="bold/question-help-circle" v-tooltip="$t('configs.rightPanel.messages.crmAppointmentConfirmationTooltip')" />
                        <language-chooser class="link-gray | ml-3" :locales="locales" v-model="activeLocale" />
                    </template>
                    <template #right-label>
                        <attributes-dropdown placement="left" @insert="insertAttribute" />
                    </template>
                    <div class="flex flex-col border border-gray-200 rounded">
                        <div class="flex items-stretch">
                            <div class="flex-1">
                                <nio-textarea
                                    class="h-full rounded-tl rounded-b-none rounded-r-none border-none"
                                    rows="3"
                                    ref="confirmationMessage"
                                    v-model="user.options.crm_confirmation_message[activeLocale]"
                                />
                            </div>
                            <div class="flex-1 rounded-tr border-l border-gray-200">
                                <p class="px-4 py-2" v-html="messagePreview" />
                            </div>
                        </div>
                        <div class="flex items-center p-2 w-full border-t border-gray-200">
                            <nio-toggle v-model="user.options.append_confirmation_message">
                                <span v-text="$t('configs.rightPanel.messages.appendConfirmationMessage')" />
                            </nio-toggle>
                        </div>
                    </div>
                </nio-input-group>

                <nio-alert type="error" v-if="displayCrmConfirmationError">
                    <p v-text="$t('configs.rightPanel.messages.crmConfirmationError')" />
                </nio-alert>
                <nio-alert type="info" v-else-if="displayCrmConfirmationWarning">
                    <p v-text="$t('configs.rightPanel.messages.crmConfirmationWarning')" />
                </nio-alert>
            </div>
        </section>
    </div>
</template>

<script>
    import MessageParsing from '@/mixins/MessageParsing.js';
    import AttributesDropdown from '@/components/AttributesDropdown.vue';
    import LanguageChooser from '@/components/LanguageChooser.vue';
    import { nl2br } from '@/utils';

    export default {
        components: {
            AttributesDropdown,
            LanguageChooser,
        },

        mixins: [MessageParsing],

        props: {
            user: {
                type: Object,
                required: true,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            showPaymentMessage: {
                type: Boolean,
                required: true,
            },
        },

        data() {
            return {
                activeLocale: 'fr',
            };
        },

        computed: {
            displayUnsubscribeWarning() {
                if (this.loading) {
                    return false;
                }

                return this.activeLocale == 'fr' && !this.user.options.unsubscribe_message.fr ||
                    this.activeLocale == 'en' && !this.user.options.unsubscribe_message.en;
            },

            displayCrmConfirmationWarning() {
                if (this.loading) {
                    return false;
                }

                return !this.user.options.crm_confirmation_message.fr ||
                    !this.user.options.crm_confirmation_message.en;
            },

            displayCrmConfirmationError() {
                if (this.loading) {
                    return false;
                }

                return !this.user.options.crm_confirmation_message.en &&
                    !this.user.options.crm_confirmation_message.fr;
            },

            exampleAppointment() {
                return {
                    datetime: moment().format(),
                    contact: {
                        civility: 'mr',
                        first_name: 'John',
                        last_name: 'Doe',
                        locale: this.activeLocale,
                    },
                    contact_phone: {
                        number: '14501231234',
                    },
                    amount: 9999,
                    invoice_number: 'INV001',
                };
            },

            messagePreview() {
                let message = this.fillTemplateValues(this.user.options.crm_confirmation_message[this.activeLocale], this.exampleAppointment);

                if (this.user.options.append_confirmation_message) {
                    if (message) {
                        message += '<br>';
                    }

                    message += this.user.options.confirmation_message[this.activeLocale];
                }

                return nl2br(message);
            },

            locales() {
                return [
                    { value: 'fr', label: 'Français' },
                    { value: 'en', label: 'English' },
                ];
            },
        },

        methods: {
            insertAttribute(attribute) {
                this.$refs.confirmationMessage.insertAtCaret(attribute);
            },
        },
    };
</script>
